/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  FormFeedback,
  Button,
} from "reactstrap";
import { Link } from "react-router-dom";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

//Import Breadcrumb
import BreadCrumb from "../../Components/Common/BreadCrumb";
import dummyImg from "../../assets/images/users/user-dummy-img.jpg";
import TableContainer from "../../Components/Common/TableContainer";
import {
  createVisitFormBanner,
  deleteVisitFormBanner,
  listVisitFormBanner,
  updateVisitFormBanner,
} from "./_requests";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useProfile } from "../../Components/Hooks/UserHooks";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CropModal from "../../Components/Common/CropModal";

const VisitFormBanner = () => {
  const { userProfile } = useProfile();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [banners, setBanners] = useState<any>([]);
  const [modal, setModal] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [bannerToDeleteId, setBannerToDeleteId] = useState<string>("");
  const [bannerToDeleteName, setBannerToDeleteName] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<any>();
  const [previewImage, setPreviewImage] = useState<string | null>("");
  const [imageError, setImageError] = useState<string | null>(null);

  // Cropper states
  const [cropModal, setCropModal] = useState<boolean>(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);

  const toggle = useCallback(() => {
    setModal(!modal);
  }, [modal]);

  const toggleDeleteModal = useCallback(() => {
    setDeleteModalOpen(!deleteModalOpen);
  }, [deleteModalOpen]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setImageToCrop(e.target.result as string);
          setCropModal(true);
        }
      };
      reader.readAsDataURL(file);
      // Reset input value so same file can be selected again
      event.target.value = "";
    }
  };

  const handleCropSave = (croppedFile: File) => {
    setSelectedImage(croppedFile);
    validation.setFieldValue("image", croppedFile);
    setPreviewImage(URL.createObjectURL(croppedFile));
    setImageError(null);
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const res = await listVisitFormBanner();
      setBanners(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch banners");
    }
  };

  const onClickDelete = useCallback(
    (banner: any) => {
      setBannerToDeleteName(banner.title);
      setBannerToDeleteId(banner.id);
      toggleDeleteModal();
    },
    [toggleDeleteModal]
  );

  const confirmDelete = async (bannerId: any) => {
    try {
      const res = await deleteVisitFormBanner(bannerId, userProfile?.userId);
      toast.success(res.message, { autoClose: 3000 });
      fetchBanners();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete banner");
    }
    toggleDeleteModal();
  };

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: "",
      title: "",
      image: "",
      description: "",
      button_name: "",
      button_link: "",
      mobile_number: "",
      list_order: 0,
      enable_disable: true,
    },
    validationSchema: Yup.object().shape({
      title: Yup.string()
        .required("Title is required")
        .test("min-words", "Title must have at least 3 words", (value) => {
          if (!value) return false;
          const text = value.replace(/<[^>]*>/g, "").trim();
          return text.split(/\s+/).filter((word) => word.length > 0).length >= 3;
        })
        .test("max-words", "Title must not exceed 25 words", (value) => {
          if (!value) return true;
          const text = value.replace(/<[^>]*>/g, "").trim();
          return text.split(/\s+/).filter((word) => word.length > 0).length <= 25;
        })
        .max(255, "Title must not exceed 255 characters"),
      image: Yup.mixed().test(
        "image-required",
        "Image is required",
        function (value) {
          if (!isEdit || !previewImage) {
            return !!value;
          }
          return true;
        }
      ),
      mobile_number: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
        .nullable(),
    }),
    onSubmit: async (values: any) => {
      if (imageError) {
        toast.error("Please select a valid image with 160x160 px dimensions.");
        return;
      }
      try {
        const formData = new FormData();
        formData.append("title", values.title);
        if (values.image) {
          formData.append("image", values.image);
        }
        formData.append("description", values.description || "");
        formData.append("button_name", values.button_name || "");
        formData.append("button_link", values.button_link || "");
        formData.append("mobile_number", values.mobile_number || "");
        formData.append("list_order", values.list_order || "0");
        formData.append("enable_disable", values.enable_disable ? "1" : "0");
        formData.append(isEdit ? "updated_by" : "created_by", userProfile?.userId || "0");

        const actionPromise = isEdit
          ? updateVisitFormBanner(formData, values.id)
          : createVisitFormBanner(formData);

        const res = await actionPromise;
        toast.success(res.message, { autoClose: 3000 });
        fetchBanners();
        validation.resetForm();
        toggle();
      } catch (error) {
        console.error("Error:", error);
        toast.error("Something went wrong", { autoClose: 3000 });
      }
    },
  });

  const handleBannerClick = useCallback(
    (banner: any) => {
      validation.setValues({
        id: banner.id,
        title: banner.title,
        image: "",
        description: banner.description,
        button_name: banner.button_name,
        button_link: banner.button_link,
        mobile_number: banner.mobile_number,
        list_order: banner.list_order || 0,
        enable_disable: banner.enable_disable === 1,
      });
      setPreviewImage(banner.image);
      setImageError(null);
      setIsEdit(true);
      toggle();
    },
    [toggle, validation]
  );

  const columns = useMemo(
    () => [
      {
        header: "S.No",
        id: "sno",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const { pageIndex, pageSize } = cellProps.table.getState().pagination;
          return pageIndex * pageSize + cellProps.row.index + 1;
        },
      },
      {
        header: "Image",
        accessorKey: "image",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <div className="avatar-sm bg-light rounded p-1">
            <img
              src={cell.row.original.image || dummyImg}
              alt=""
              className="img-fluid d-block"
              style={{ maxHeight: '100%', objectFit: 'contain' }}
            />
          </div>
        ),
      },
      {
        header: "Title",
        accessorKey: "title",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <div 
            style={{ minWidth: "250px", maxWidth: "450px", whiteSpace: "normal" }} 
            dangerouslySetInnerHTML={{ __html: cell.getValue() || "" }} 
          />
        ),
      },
      {
        header: "List Order",
        accessorKey: "list_order",
        enableColumnFilter: false,
      },
      {
        header: "Mobile",
        accessorKey: "mobile_number",
        enableColumnFilter: false,
      },
      {
        header: "Action",
        cell: (cellProps: any) => {
          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <li className="list-inline-item edit" title="Edit">
                <Link
                  to="#"
                  className="text-primary d-inline-block edit-item-btn"
                  onClick={() => handleBannerClick(cellProps.row.original)}
                >
                  <i className="ri-pencil-fill fs-16"></i>
                </Link>
              </li>
              <li className="list-inline-item" title="Remove">
                <Link
                  to="#"
                  className="text-danger d-inline-block remove-item-btn"
                  onClick={() => onClickDelete(cellProps.row.original)}
                >
                  <i className="ri-delete-bin-5-fill fs-16"></i>
                </Link>
              </li>
            </ul>
          );
        },
      },
    ],
    [handleBannerClick, onClickDelete]
  );

  const addBanner = () => {
    setIsEdit(false);
    
    // Calculate the next list order based on current count
    const nextOrder = (Array.isArray(banners) ? banners.length : 0) + 1;

    validation.resetForm();
    validation.setValues({
      id: "",
      title: "",
      image: "",
      description: "",
      button_name: "",
      button_link: "",
      mobile_number: "",
      list_order: nextOrder,
      enable_disable: true,
    });

    setPreviewImage("");
    setSelectedImage(null);
    setImageError(null);
    toggle();
  };

  document.title = "Visit Form Banner | Paal Dappa Admin";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Visit Form Banner" pageTitle="Dashboard" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <h5 className="card-title mb-0">Visit Form Banner List</h5>
                    </div>
                    <div className="col-sm-auto">
                      <button
                        type="button"
                        className="btn text-white add-btn"
                        style={{ backgroundColor: "#28a8df" }}
                        onClick={addBanner}
                      >
                        <i className="ri-add-line align-bottom me-1"></i> Add Banner
                      </button>
                    </div>
                  </Row>
                </CardHeader>
                <div className="card-body pt-0">
                  <TableContainer
                    columns={columns}
                    data={banners || []}
                    isGlobalFilter={true}
                    customPageSize={10}
                    divClass="table-responsive table-card mb-3"
                    tableClass="align-top table-bordered align-top table-nowrap mb-0"
                    SearchPlaceholder="Search Banner..."
                  />

                  <Modal isOpen={modal} toggle={toggle} centered size="lg">
                    <ModalHeader className="bg-light p-3" toggle={toggle}>
                      {!!isEdit ? "Edit Visit Form Banner" : "Add Visit Form Banner"}
                    </ModalHeader>
                    <Form
                      className="tablelist-form"
                      onSubmit={(e: any) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <ModalBody>
                        <div className="mb-4 text-center">
                          <div className="d-inline-block">
                            <div className="position-relative mb-2">
                              {/* Standardized Preview Container */}
                              <div className="border rounded bg-light p-2 mx-auto" style={{ width: '120px', height: '120px' }}>
                                <div className="w-100 h-100 d-flex align-items-center justify-content-center overflow-hidden">
                                  <img
                                    src={previewImage || dummyImg}
                                    alt="Preview"
                                    className="img-fluid rounded"
                                    style={{ maxHeight: '100px', objectFit: 'cover' }}
                                  />
                                </div>
                              </div>

                              {/* Action Button Group */}
                              <div className="mt-3 d-flex justify-content-center gap-2">
                                <Label
                                  htmlFor="banner-image-input"
                                  className="btn btn-light btn-sm border d-flex align-items-center gap-1 cursor-pointer mb-0"
                                  title="Upload Image"
                                >
                                  <i className="ri-image-add-line"></i>
                                  {previewImage && previewImage !== dummyImg ? "Change" : "Select"}
                                </Label>

                                {previewImage && previewImage !== dummyImg && (
                                  <Button
                                    type="button"
                                    color="primary"
                                    size="sm"
                                    outline
                                    className="d-flex align-items-center gap-1"
                                    onClick={() => {
                                      setImageToCrop(previewImage);
                                      setCropModal(true);
                                    }}
                                    title="Crop Image"
                                  >
                                    <i className="ri-crop-2-line"></i> Crop
                                  </Button>
                                )}
                              </div>

                              <Input
                                name="image"
                                className="form-control d-none"
                                id="banner-image-input"
                                type="file"
                                accept="image/png, image/jpeg"
                                onChange={handleImageChange}
                              />
                            </div>
                          </div>
                          {validation.touched.image && validation.errors.image && (
                            <div className="text-danger small mt-2">{validation.errors.image}</div>
                          )}
                          {imageError && (
                            <div className="text-danger small mt-2">{imageError}</div>
                          )}
                          <div className="mt-2 text-muted small">
                            <i className="ri-information-line me-1"></i>
                            Exact size: 160x160 px
                          </div>
                        </div>

                        <Row>
                          <Col md={12}>
                            <div className="mb-3">
                              <Label htmlFor="title-field" className="form-label">
                                Title <span className="text-danger">*</span>
                              </Label>
                              <ReactQuill
                                theme="snow"
                                value={validation.values.title || ""}
                                onChange={(value) => validation.setFieldValue("title", value)}
                                onBlur={() => validation.setFieldTouched("title", true)}
                                placeholder="Enter Title"
                              />
                              {validation.touched.title && validation.errors.title && (
                                <div className="text-danger small mt-1">{validation.errors.title}</div>
                              )}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Row className="align-items-center">
                                <Col md={6}>
                                  <Label htmlFor="mobile-field" className="form-label mb-0">
                                    Mobile Number
                                  </Label>
                                </Col>
                                <Col md={6}>
                                  <Input
                                    name="mobile_number"
                                    id="mobile-field"
                                    className="form-control"
                                    placeholder="Enter Mobile Number"
                                    type="text"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.mobile_number || ""}
                                    invalid={validation.touched.mobile_number && !!validation.errors.mobile_number}
                                  />
                                  {validation.touched.mobile_number && validation.errors.mobile_number && (
                                    <FormFeedback>{validation.errors.mobile_number}</FormFeedback>
                                  )}
                                </Col>
                              </Row>
                            </div>
                          </Col>
                        </Row>

                        <div className="mb-3">
                          <Label htmlFor="description-field" className="form-label">
                            Description
                          </Label>
                          <ReactQuill
                            theme="snow"
                            value={validation.values.description || ""}
                            onChange={(value) => validation.setFieldValue("description", value)}
                            onBlur={() => validation.setFieldTouched("description", true)}
                            placeholder="Enter Description"
                          />
                        </div>

                        <Row>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label htmlFor="button-name-field" className="form-label">
                                Button Name
                              </Label>
                              <Input
                                name="button_name"
                                id="button-name-field"
                                className="form-control"
                                placeholder="Enter Button Name"
                                type="text"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.button_name || ""}
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label htmlFor="button-link-field" className="form-label">
                                Button Link
                              </Label>
                              <Input
                                name="button_link"
                                id="button-link-field"
                                className="form-control"
                                placeholder="Enter Button Link"
                                type="text"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.button_link || ""}
                              />
                            </div>
                          </Col>
                        </Row>

                        <div className="mb-3">
                          <Label htmlFor="list-order-field" className="form-label">
                            List Order
                          </Label>
                          <Input
                            name="list_order"
                            id="list-order-field"
                            className="form-control"
                            placeholder="Enter List Order"
                            type="number"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.list_order || 0}
                          />
                        </div>

                        <div className="mb-3">
                          <div className="form-check form-switch form-switch-md">
                            <Input
                              name="enable_disable"
                              className="form-check-input"
                              type="checkbox"
                              id="enable_disable_switch"
                              onChange={validation.handleChange}
                              checked={validation.values.enable_disable}
                            />
                            <Label className="form-check-label" htmlFor="enable_disable_switch">
                              Enable Banner
                            </Label>
                          </div>
                        </div>
                      </ModalBody>
                      <ModalFooter>
                        <Button color="light" onClick={toggle}>Cancel</Button>
                        <Button type="submit" style={{ backgroundColor: "#28a8df", borderColor: "#28a8df" }} className="text-white">
                          {isEdit ? "Update Banner" : "Add Banner"}
                        </Button>
                      </ModalFooter>
                    </Form>
                  </Modal>

                  <Modal isOpen={deleteModalOpen} toggle={toggleDeleteModal} centered>
                    <ModalHeader toggle={toggleDeleteModal}>Confirm Deletion</ModalHeader>
                    <ModalBody>
                      Are you sure you want to delete the banner
                      <strong dangerouslySetInnerHTML={{ __html: bannerToDeleteName }}></strong>?
                    </ModalBody>
                    <ModalFooter>
                      <Button color="light" onClick={toggleDeleteModal}>Cancel</Button>
                      <Button color="danger" onClick={() => confirmDelete(bannerToDeleteId)}>Delete</Button>
                    </ModalFooter>
                  </Modal>

                  {/* Crop Modal */}
                  <CropModal
                    isOpen={cropModal}
                    toggle={() => setCropModal(false)}
                    imageSrc={imageToCrop}
                    onCropSave={handleCropSave}
                    targetWidth={160}
                    targetHeight={160}
                    title="Visit Form Banner Crop"
                  />
                </div>
              </Card>
            </Col>
          </Row>
          <ToastContainer closeButton={false} limit={1} />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default VisitFormBanner;
